import { useEffect, useState } from "react";
import { Flame } from "lucide-react";
import { api } from "@/lib/api";
import { CompanySheet, coKey } from "@/components/radar/CompanySheet";

const DAY = 86400000;
const wfOf = (s) => s.workflow_name || s.workflow_id || null;
const dt = (s) => new Date(s.published_at || s.created_at || 0).getTime();

// Fallback: companies hit by 2+ different workflows in the last 30 days, from GET /signals.
const fromSignals = (sigs) => {
  const m = new Map();
  sigs.filter((s) => wfOf(s) && dt(s) > Date.now() - 30 * DAY).forEach((s) => {
    const k = coKey(s.company_name); if (!k) return;
    const g = m.get(k) || { key: k, name: s.company_name, domain: s.company_domain, id: s.company_id, workflows: new Set(), signals: [], last: 0 };
    g.workflows.add(wfOf(s)); g.signals.push(s); g.last = Math.max(g.last, dt(s)); g.id ||= s.company_id; g.domain ||= s.company_domain;
    m.set(k, g);
  });
  return [...m.values()].filter((g) => g.workflows.size >= 2).map((g) => ({ ...g, workflows: [...g.workflows] }));
};

export const HotToday = () => {
  const [items, setItems] = useState(null);
  const [idx, setIdx] = useState(null);
  useEffect(() => {
    api.get("/admin/rcc/radar/hot-today?days=30").then((r) => (r.data?.items || []).map((x) => ({ key: x.company_id || coKey(x.name), id: x.company_id, name: x.name, domain: x.domain, workflows: (x.workflows || []).map((w) => w.name || w), signals: x.signals || [], last: new Date(x.last_signal_at || 0).getTime(), score: x.score })))
      .catch((e) => (e.response?.status === 404 ? api.get("/admin/rcc/signals?limit=500").then((r) => fromSignals(r.data?.items || [])) : []))
      .then((xs) => setItems(xs.sort((a, b) => b.last - a.last || b.workflows.length - a.workflows.length)))
      .catch(() => setItems([]));
  }, []);
  if (!items?.length) return null;
  return (
    <div className="rounded-2xl border border-[#F97316]/40 bg-gradient-to-br from-[#7C2D12]/30 to-transparent p-4" data-testid="radar-hot">
      <p className="flex flex-wrap items-center gap-x-2 gap-y-0.5 text-sm font-semibold text-zinc-50"><Flame className="h-4 w-4 text-[#FB923C]" />Hot today<span className="basis-full text-[11px] font-normal text-zinc-400 sm:basis-auto" data-testid="radar-hot-count">{items.length} compan{items.length === 1 ? "y" : "ies"} hit by 2+ workflows in the last 30 days. Call these first.</span></p>
      <div className="mt-3 grid gap-2 md:grid-cols-2">{items.map((g, i) => (
        <button key={g.key} onClick={() => setIdx(i)} className="rounded-xl border border-[#27272A] bg-[#0f0f12] p-3 text-left transition-colors hover:border-[#FB923C]/60" data-testid={`radar-hot-${i}`}>
          <div className="flex items-center gap-2"><span className="min-w-0 flex-1 truncate text-sm font-semibold text-zinc-100">{g.name}</span><span className="shrink-0 rounded-full bg-[#F97316]/20 px-2 py-0.5 text-[10px] font-semibold text-[#FDBA74]" data-testid={`radar-hot-n-${i}`}>{g.workflows.length} workflows</span></div>
          <div className="mt-1.5 flex flex-wrap gap-1">{g.workflows.map((w) => <span key={w} className="max-w-full truncate rounded-full border border-[#8224e3]/40 px-2 py-0.5 text-[10px] text-[#d7b6ff]">{w}</span>)}</div>
          {g.signals[0] && <p className="mt-1.5 truncate text-[11px] text-zinc-400">{g.signals[0].title || g.signals[0].description}</p>}
          <p className="mt-1 text-[10px] text-zinc-600">Latest {g.last ? new Date(g.last).toLocaleDateString() : "—"}</p>
        </button>))}</div>
      <CompanySheet list={items} index={idx} setIndex={setIdx} onClose={() => setIdx(null)} />
    </div>
  );
};
